import type { Paginated } from '@helios/data-access';
import { apiClient, apiOrigin, type ApiResult, type RequestOptions } from '@/services/apiClient';

export type NotificationType = 'INFO' | 'SUCCESS' | 'WARNING' | 'CRITICAL' | 'SYSTEM';
export type NotificationChannel = 'IN_APP' | 'EMAIL' | 'SMS' | 'WEBHOOK';

export interface UserSettings {
  id: string;
  email: string;
  name: string;
  title: string | null;
  version: number;
  implementationStatus?: { emailDigestDelivery: 'UNAVAILABLE'; criticalOnlyNotifications: 'ACTIVE' };
  preferences: {
    timezone: string;
    dateFormat: string;
    landingPage: string;
    denseMode: boolean;
    emailDigestEnabled: boolean;
    criticalOnlyNotifications: boolean;
  };
}

export interface OrganizationSettings {
  id: string;
  name: string;
  slug: string;
  type: string | null;
  version: number;
  implementationStatus?: { timezone: 'ACTIVE'; fiscalYearStartMonth: 'ACTIVE'; auditRetentionYears: 'POLICY_ONLY'; requireMfa: 'POLICY_ONLY'; allowExternalSharing: 'POLICY_ONLY'; defaultNotificationChannel: 'POLICY_ONLY' };
  settings: {
    timezone: string;
    fiscalYearStartMonth: number;
    auditRetentionYears: number;
    requireMfa: boolean;
    allowExternalSharing: boolean;
    defaultNotificationChannel: NotificationChannel;
  };
}

export interface FeatureFlagItem {
  id: string;
  key: string;
  name: string;
  description: string | null;
  category: string;
  enabled: boolean;
  rolloutPercent: number;
  locked: boolean;
  updatedAt: string;
  version: number;
}

export interface SettingsBootstrap {
  generatedAt: string;
  tenant: { id: string; name: string; slug: string; plan: string | null; status: string };
  user: UserSettings;
  organization: OrganizationSettings | null;
  featureFlags: FeatureFlagItem[];
  notifications: {
    unread: number;
    total: number;
    criticalUnread: number;
  };
  audit: {
    total: number;
    last24Hours: number;
  };
  permissions: {
    canManageSettings: boolean;
    canManageFeatureFlags: boolean;
    canReadAuditLogs: boolean;
  };
}

export interface NotificationItem {
  id: string;
  type: NotificationType;
  channel: NotificationChannel;
  title: string;
  body: string;
  entityType: string | null;
  entityId: string | null;
  actionUrl: string | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationSummary {
  unread: number;
  total: number;
  criticalUnread: number;
}

export interface AuditLogItem {
  id: string;
  tenantId: string | null;
  actorId: string | null;
  actor: { id: string; name: string; email: string } | null;
  action: string;
  entityType: string;
  entityId: string | null;
  ipAddress: string | null;
  requestId: string | null;
  createdAt: string;
}

export type UpdateUserSettingsInput = Partial<Omit<UserSettings['preferences'], 'emailDigestEnabled'>> & {
  expectedVersion: number;
  name?: string;
  title?: string;
};

export type UpdateOrganizationSettingsInput = Partial<OrganizationSettings['settings']> & {
  expectedVersion: number;
  name?: string;
  type?: string | null;
};

export interface UpdateFeatureFlagInput {
  expectedVersion: number;
  enabled?: boolean;
  rolloutPercent?: number;
}

export class SettingsApiError extends Error {
  constructor(message: string, readonly status: number) { super(message); this.name = 'SettingsApiError'; }
}

export interface NotificationQuery { page: number; pageSize: number; status?: 'all' | 'read' | 'unread'; type?: NotificationType; channel?: NotificationChannel }

function expectData<T>(result: ApiResult<T>): T {
  if (!result.ok || !result.data) {
    throw new SettingsApiError(result.error ?? 'API request failed', result.status);
  }
  return result.data;
}

async function getSettingsBootstrap(options?: RequestOptions): Promise<SettingsBootstrap> {
  return expectData(await apiClient.get<SettingsBootstrap>(`${apiOrigin}/api/v1/settings/bootstrap`, options));
}

async function getOwnSettings(options?: RequestOptions): Promise<UserSettings> {
  return expectData(await apiClient.get<UserSettings>(`${apiOrigin}/api/v1/settings/me`, options));
}

async function updateUserSettings(input: UpdateUserSettingsInput, options?: RequestOptions): Promise<UserSettings> {
  return expectData(await apiClient.patch<UserSettings>(`${apiOrigin}/api/v1/settings/me`, input, options));
}

async function updateOrganizationSettings(input: UpdateOrganizationSettingsInput, options?: RequestOptions): Promise<OrganizationSettings> {
  return expectData(await apiClient.patch<OrganizationSettings>(`${apiOrigin}/api/v1/settings/organization`, input, options));
}

async function updateFeatureFlag(key: string, input: UpdateFeatureFlagInput, options?: RequestOptions): Promise<FeatureFlagItem> {
  return expectData(await apiClient.patch<FeatureFlagItem>(`${apiOrigin}/api/v1/settings/feature-flags/${encodeURIComponent(key)}`, input, options));
}

async function listNotifications(query: NotificationQuery = { page: 1, pageSize: 8 }, options?: RequestOptions): Promise<Paginated<NotificationItem>> {
  const search = new URLSearchParams({ page: String(query.page), pageSize: String(query.pageSize) });
  if (query.status) search.set('status', query.status);
  if (query.type) search.set('type', query.type);
  if (query.channel) search.set('channel', query.channel);
  return expectData(await apiClient.get<Paginated<NotificationItem>>(`${apiOrigin}/api/v1/notifications?${search}`, options));
}

async function getNotificationsSummary(options?: RequestOptions): Promise<NotificationSummary> {
  return expectData(await apiClient.get<NotificationSummary>(`${apiOrigin}/api/v1/notifications/summary`, options));
}

async function markNotificationRead(id: string, options?: RequestOptions): Promise<NotificationItem> {
  return expectData(await apiClient.patch<NotificationItem>(`${apiOrigin}/api/v1/notifications/${encodeURIComponent(id)}/read`, undefined, options));
}

async function markAllNotificationsRead(options?: RequestOptions): Promise<{ updated: number }> {
  return expectData(await apiClient.post<{ updated: number }>(`${apiOrigin}/api/v1/notifications/mark-all-read`, undefined, options));
}

async function createTestNotification(options?: RequestOptions): Promise<NotificationItem> {
  return expectData(await apiClient.post<NotificationItem>(`${apiOrigin}/api/v1/notifications/test`, undefined, options));
}

async function listAuditLogs(query: {
  page: number;
  pageSize?: number;
  tenantId?: string;
  action?: string;
  entityType?: string;
}, options?: RequestOptions): Promise<Paginated<AuditLogItem>> {
  const search = new URLSearchParams();
  search.set('page', String(query.page));
  search.set('pageSize', String(query.pageSize ?? 20));
  if (query.tenantId) search.set('tenantId', query.tenantId);
  if (query.action) search.set('action', query.action);
  if (query.entityType) search.set('entityType', query.entityType);
  return expectData(
    await apiClient.get<Paginated<AuditLogItem>>(
      `${apiOrigin}/api/v1/system/audit-logs?${search.toString()}`,
      options,
    ),
  );
}

export const settingsClient = {
  getSettingsBootstrap,
  getOwnSettings,
  updateUserSettings,
  updateOrganizationSettings,
  updateFeatureFlag,
  listNotifications,
  getNotificationsSummary,
  markNotificationRead,
  markAllNotificationsRead,
  createTestNotification,
  listAuditLogs,
};
